import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DollarSign, AlertTriangle, Clock, TrendingUp, Users, FileText,
  ChevronDown, ChevronRight, ArrowUpRight, CheckCircle,
  Mail, Send, Settings, X, Plus, Loader2, Activity, Download, ExternalLink, FileBarChart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, LabelList
} from 'recharts';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { ReminderPanel } from './ObjectivesAndReminders';
import BillingAlertWidget from './BillingAlertWidget';
import ClientStatementDialog from './ClientStatementDialog';
import StatementGeneratorModal from './StatementGeneratorModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGING_COLORS = ['#22c55e', '#f59e0b', '#f97316', '#ef4444'];

const formatAmount = (value) => {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}Md`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return value?.toLocaleString('fr-FR') || '0';
};

// Exact amount formatter for KPI cards & critical financial views (no rounding)
const formatExact = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return Math.round(value).toLocaleString('fr-FR');
};

const PARENT_PERIOD_MAP = {
  '7d': '7d', '30d': '1m', '90d': '3m', '12m': '1y', 'ytd': '1y', 'all': 'all'
};

const RecouvrementKPIs = ({ token, period: externalPeriod }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDebtor, setExpandedDebtor] = useState(null);
  // Report config state
  const [showConfig, setShowConfig] = useState(false);
  const [reportConfig, setReportConfig] = useState({ enabled: false, recipients: [], day_of_week: 1 });
  const [newEmail, setNewEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Receivables filter state (must be before any conditional return)
  const [recSearch, setRecSearch] = useState('');
  const [recAgingFilter, setRecAgingFilter] = useState('all');
  const [recSortField, setRecSortField] = useState('daysOutstanding');
  const [recSortDir, setRecSortDir] = useState(-1);

  // Reminder history state
  const [reminderLogs, setReminderLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const [logForm, setLogForm] = useState({ operationId: '', invoiceRef: '', clientName: '', channel: 'call', note: '' });

  // Statement (Relevé de compte) state
  const [statementGenOpen, setStatementGenOpen] = useState(false);
  const [statementDialogOpen, setStatementDialogOpen] = useState(false);
  const [statementTarget, setStatementTarget] = useState(null);

  const openStatementForClient = useCallback((client) => {
    setStatementTarget(client);
    setStatementDialogOpen(true);
  }, []);

  const DAYS = [
    { value: '0', label: 'Lundi' }, { value: '1', label: 'Mardi' },
    { value: '2', label: 'Mercredi' }, { value: '3', label: 'Jeudi' },
    { value: '4', label: 'Vendredi' }, { value: '5', label: 'Samedi' },
    { value: '6', label: 'Dimanche' },
  ];

  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/collection-report/config`, { headers });
      setReportConfig(res.data);
    } catch (e) { /* ignore */ }
  }, [token]);

  useEffect(() => { if (token) fetchConfig(); }, [token, fetchConfig]);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await axios.put(`${API}/admin/collection-report/config`, {
        enabled: reportConfig.enabled,
        recipients: reportConfig.recipients,
        day_of_week: reportConfig.day_of_week,
      }, { headers });
      setReportConfig(res.data);
      toast({ title: 'Configuration sauvegardée' });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder la configuration', variant: 'destructive' });
    }
    setSavingConfig(false);
  };

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (email && email.includes('@') && !reportConfig.recipients.includes(email)) {
      setReportConfig(prev => ({ ...prev, recipients: [...prev.recipients, email] }));
      setNewEmail('');
    }
  };

  const removeRecipient = (email) => {
    setReportConfig(prev => ({ ...prev, recipients: prev.recipients.filter(e => e !== email) }));
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/collection-report/send`, {}, { headers });
      if (res.data?.success) {
        toast({ title: 'Rapport envoyé', description: `Envoyé à ${res.data.sent_count} destinataire(s)` });
      } else {
        toast({ title: 'Échec', description: res.data?.error || 'Erreur inconnue', variant: 'destructive' });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erreur lors de l\'envoi';
      toast({ title: 'Erreur d\'envoi', description: msg, variant: 'destructive' });
    }
    setSending(false);
    fetchConfig();
  };

  const activePeriod = PARENT_PERIOD_MAP[externalPeriod] || externalPeriod || 'all';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/dashboard/recouvrement-kpis`, {
          params: { period: activePeriod }});
        setData(res.data);
      } catch (e) {
        console.error('Error fetching recouvrement KPIs:', e);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token, activePeriod]);

  // Fetch reminder logs
  const fetchReminderLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/admin/reminders/logs?limit=50`, {});
      setReminderLogs(res.data?.logs || res.data || []);
    } catch (err) { console.error('Logs fetch error:', err); }
    finally { setLoadingLogs(false); }
  }, [token]);

  useEffect(() => { if (token) fetchReminderLogs(); }, [fetchReminderLogs]);

  const handleLogManualReminder = async () => {
    if (!logForm.clientName || !logForm.note) return;
    try {
      await axios.post(`${API}/admin/reminders/logs/manual`, logForm, {});
      setShowLogForm(false);
      setLogForm({ operationId: '', invoiceRef: '', clientName: '', channel: 'call', note: '' });
      fetchReminderLogs();
    } catch (err) { console.error('Log error:', err); }
  };


  const handleDownloadInvoice = async (e, inv) => {
    e.stopPropagation();
    if (!inv.operationId || inv.type !== 'operation') return;
    try {
      const res = await axios.post(`${API}/documents/generate/invoice/${inv.operationId}`, {}, {
        responseType: 'blob'});
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${inv.reference || 'facture'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erreur lors du téléchargement de la facture');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  if (!data) {
    return <p className="text-center text-gray-500 py-8">Impossible de charger les KPIs recouvrement</p>;
  }

  const { summary, aging, topDebtors, recentPayments, allReceivables } = data;
  const agingGranular = data.agingGranular || [];
  const monthlyUnpaid = data.monthlyUnpaid || [];
  const fastPayers = data.fastPayers || [];
  const inconsistencies = data.inconsistencies || [];
  const forecast = data.forecast || { weeks: [], overdueAmount: 0, overdueCount: 0 };
  const clientDSO = data.clientDSO || [];
  const contractWIP = data.contractWIP || { totalCount: 0, totalAmount: 0, byContract: [] };
  const billingVariance = data.billingVariance || { totalDelta: 0, count: 0, byReason: {}, items: [] };

  const filteredReceivables = (allReceivables || []).filter(inv => {
    if (recSearch) {
      const q = recSearch.toLowerCase();
      if (!(inv.reference || '').toLowerCase().includes(q) &&
          !(inv.clientName || '').toLowerCase().includes(q) &&
          !(inv.operationNumber || '').toLowerCase().includes(q)) return false;
    }
    if (recAgingFilter !== 'all') {
      const d = inv.daysOutstanding || 0;
      if (recAgingFilter === '0-30' && d > 30) return false;
      if (recAgingFilter === '30-60' && (d <= 30 || d > 60)) return false;
      if (recAgingFilter === '60-90' && (d <= 60 || d > 90)) return false;
      if (recAgingFilter === '90+' && d <= 90) return false;
    }
    return true;
  }).sort((a, b) => {
    if (recSortField === 'daysOutstanding') return (a.daysOutstanding - b.daysOutstanding) * recSortDir;
    if (recSortField === 'remaining') return (a.remaining - b.remaining) * recSortDir;
    if (recSortField === 'clientName') return (a.clientName || '').localeCompare(b.clientName || '') * recSortDir;
    if (recSortField === 'totalAmount') return (a.totalAmount - b.totalAmount) * recSortDir;
    return 0;
  });

  const toggleRecSort = (field) => {
    if (recSortField === field) setRecSortDir(d => d * -1);
    else { setRecSortField(field); setRecSortDir(-1); }
  };

  const agingChartData = [
    { name: '0-30j', value: aging.current.amount, count: aging.current.count, fill: AGING_COLORS[0] },
    { name: '30-60j', value: aging['30_60'].amount, count: aging['30_60'].count, fill: AGING_COLORS[1] },
    { name: '60-90j', value: aging['60_90'].amount, count: aging['60_90'].count, fill: AGING_COLORS[2] },
    { name: '>90j', value: aging.over_90.amount, count: aging.over_90.count, fill: AGING_COLORS[3] },
  ];

  const recoveryPieData = [
    { name: 'Encaisse', value: summary.totalCollected },
    { name: 'Impaye', value: summary.totalOutstanding },
  ];

  return (
    <div className="space-y-6" data-testid="recouvrement-kpis">

      {/* ═══════ 1. SUMMARY KPIs ═══════ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Facturé</p>
                <p className="text-lg font-bold text-gray-900 mt-1 break-words leading-tight font-mono" data-testid="total-invoiced" title={`${formatExact(summary.totalInvoiced)} XOF`}>
                  {formatExact(summary.totalInvoiced)} <span className="text-xs font-normal text-gray-400">XOF</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{summary.totalInvoices} factures</p>
              </div>
              <FileText className="w-8 h-8 text-emerald-200 shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Encaissé</p>
                <p className="text-lg font-bold text-green-700 mt-1 break-words leading-tight font-mono" data-testid="total-collected" title={`${formatExact(summary.totalCollected)} XOF`}>
                  {formatExact(summary.totalCollected)} <span className="text-xs font-normal text-gray-400">XOF</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{summary.paidCount} factures payées</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200 shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total dû non recouvré</p>
                <p className="text-lg font-bold text-red-700 mt-1 break-words leading-tight font-mono" data-testid="total-outstanding" title={`${formatExact(summary.totalOutstanding)} XOF`}>
                  {formatExact(summary.totalOutstanding)} <span className="text-xs font-normal text-gray-400">XOF</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{summary.unpaidCount} factures impayées / partielles</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-200 shrink-0 ml-2" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Taux Recouvrement</p>
                <p className={`text-xl font-bold mt-1 ${summary.recoveryRate >= 70 ? 'text-green-700' : summary.recoveryRate >= 40 ? 'text-amber-600' : 'text-red-700'}`} data-testid="recovery-rate">
                  {summary.recoveryRate}%
                </p>
                <p className="text-xs text-gray-400 mt-0.5">DSO : {summary.dso} jours</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub-metrics row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-amber-700 uppercase tracking-wide font-semibold">Impayés ce mois</p>
            <p className="text-xl font-bold text-amber-800 mt-1 break-words leading-tight font-mono" data-testid="this-month-unpaid" title={`${formatExact(summary.thisMonthUnpaidAmount || 0)} XOF`}>
              {formatExact(summary.thisMonthUnpaidAmount || 0)} <span className="text-xs font-normal text-amber-600">XOF</span>
            </p>
            <p className="text-xs text-amber-600 mt-0.5">{summary.thisMonthUnpaidCount || 0} factures du mois non réglées</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-600 bg-red-50 ring-2 ring-red-100" data-testid="total-global-unpaid-card">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-red-700 uppercase tracking-wide font-semibold flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Total dû non recouvré
                </p>
                <p className="text-xl font-extrabold text-red-800 mt-1 break-words leading-tight font-mono" data-testid="total-global-unpaid" title={`${formatExact(summary.totalOutstanding || 0)} XOF`}>
                  {formatExact(summary.totalOutstanding || 0)} <span className="text-xs font-normal text-red-600">XOF</span>
                </p>
                <p className="text-xs text-red-600 mt-0.5">
                  {summary.unpaidCount} facture(s) — impayées + partielles
                </p>
                <button
                  onClick={() => navigate('/admin/operations?financialStatus=unpaid')}
                  className="text-[11px] text-red-700 hover:text-red-900 hover:underline inline-flex items-center gap-1 mt-2 font-medium"
                  data-testid="view-unpaid-invoices-btn"
                >
                  Voir les factures impayées <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500 bg-purple-50">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-purple-700 uppercase tracking-wide font-semibold">DSO moyen</p>
            <p className="text-2xl font-bold text-purple-800 mt-1">{summary.dso} jours</p>
            <p className="text-xs text-purple-600 mt-0.5">Délai moyen de paiement</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500 bg-violet-50" data-testid="contract-wip-kpi">
          <CardContent className="pt-4 pb-4">
            <p className="text-xs text-violet-700 uppercase tracking-wide font-semibold flex items-center gap-1">
              Revenus à facturer <span className="text-[10px] normal-case font-normal text-violet-600">(WIP)</span>
            </p>
            <p className="text-xl font-bold text-violet-800 mt-1 break-words leading-tight font-mono" data-testid="contract-wip-amount" title={`${formatExact(contractWIP.totalAmount || 0)} XOF`}>
              {formatExact(contractWIP.totalAmount || 0)} <span className="text-xs font-normal text-violet-600">XOF</span>
            </p>
            <p className="text-xs text-violet-600 mt-0.5">
              {contractWIP.totalCount || 0} run(s) contrat livré(s) non facturé(s)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ═══════ 2. ALERTES COMPACT ═══════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BillingAlertWidget />
        {inconsistencies.length > 0 && (
          <Card className="border border-red-200 bg-red-50/30" data-testid="inconsistencies-alert">
            <CardContent className="pt-4 pb-4 space-y-3">
              <p className="text-xs font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 text-red-500" /> Incohérences ({inconsistencies.length})
              </p>
              {inconsistencies.filter(inc => inc.type !== 'paid_no_payment' && inc.type !== 'missing_financial_status' && inc.type !== 'zero_amount_invoice').map((inc, i) => {
                // Deep-link to ops page with appropriate filter
                const targetUrl = '/admin/operations';
                return (
                  <div key={`other-${i}`} className="flex items-center justify-between bg-white border border-gray-200 px-3 py-1.5 text-xs rounded" data-testid={`inconsistency-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                      <span className="text-gray-800 text-xs">{inc.message}</span>
                    </div>
                    <button onClick={() => navigate(targetUrl)} className="text-xs text-blue-600 hover:underline flex items-center gap-1 shrink-0" data-testid={`inconsistency-view-${inc.type}`}>
                      Voir <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              {/* Bloc expandable : opérations sans statut financier */}
              {(() => {
                const noStatus = inconsistencies.find(inc => inc.type === 'missing_financial_status');
                if (!noStatus || !noStatus.items || noStatus.items.length === 0) return null;
                const isExpanded = expandedDebtor === '__no_status__';
                return (
                  <div className="bg-white border border-blue-300 rounded" data-testid="inconsistency-no-status-block">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-blue-50/50 transition-colors text-left"
                      onClick={() => setExpandedDebtor(isExpanded ? null : '__no_status__')}
                      data-testid="inconsistency-no-status-toggle"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                        <p className="text-xs font-medium text-blue-800">
                          {noStatus.items.length} opération(s) sans statut financier
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{noStatus.items.length}</Badge>
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-blue-500" /> : <ChevronRight className="w-3.5 h-3.5 text-blue-500" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t border-blue-200 max-h-[240px] overflow-y-auto">
                        <div className="flex items-center justify-between px-3 py-1.5 bg-blue-50/40 border-b border-blue-100 text-[10px] uppercase text-blue-700 font-semibold">
                          <span>Réf. / Client</span>
                          <button
                            onClick={() => navigate('/admin/operations?financialStatus=no_status')}
                            className="text-blue-600 hover:underline normal-case font-normal"
                            data-testid="inconsistency-view-missing_financial_status"
                          >
                            Traiter en liste <ExternalLink className="w-2.5 h-2.5 inline ml-0.5" />
                          </button>
                        </div>
                        {noStatus.items.map((inc, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 border-b border-blue-50 last:border-0 hover:bg-blue-50/60 text-xs" data-testid={`no-status-row-${i}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-gray-400 w-[70px] shrink-0">{inc.date || '—'}</span>
                              <span className="font-mono text-gray-700 shrink-0">{inc.reference}</span>
                              <span className="text-gray-500 truncate">{inc.client}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="font-mono text-blue-700">{inc.amount ? formatAmount(inc.amount) : '—'}</span>
                              <button
                                onClick={() => navigate(`/admin/operations/${inc.operationId}`)}
                                className="p-0.5 text-blue-600 hover:text-blue-800"
                                data-testid={`no-status-open-${inc.operationId}`}
                                title="Ouvrir le détail"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Bloc expandable : factures à montant nul (PM Spec 02/06/2026) */}
              {(() => {
                const zeroInv = inconsistencies.find(inc => inc.type === 'zero_amount_invoice');
                if (!zeroInv || !zeroInv.items || zeroInv.items.length === 0) return null;
                const isExpanded = expandedDebtor === '__zero_amount__';
                return (
                  <div className="bg-white border border-amber-300 rounded" data-testid="inconsistency-zero-amount-block">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-amber-50/50 transition-colors text-left"
                      onClick={() => setExpandedDebtor(isExpanded ? null : '__zero_amount__')}
                      data-testid="inconsistency-zero-amount-toggle"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                        <p className="text-xs font-medium text-amber-800">
                          {zeroInv.items.length} facture(s) émise(s) sans montant
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">{zeroInv.items.length}</Badge>
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-amber-500" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-500" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="border-t border-amber-200 max-h-[240px] overflow-y-auto">
                        <div className="px-3 py-1.5 bg-amber-50/40 border-b border-amber-100 text-[10px] uppercase text-amber-700 font-semibold">
                          Réf. / Client — corriger le montant pour facturation correcte
                        </div>
                        {zeroInv.items.map((inv, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 border-b border-amber-50 last:border-0 hover:bg-amber-50/60 text-xs" data-testid={`zero-amount-row-${i}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-mono text-gray-400 w-[70px] shrink-0">{inv.date || '—'}</span>
                              <span className="font-mono text-gray-700 shrink-0">{inv.reference}</span>
                              {inv.isContract && <Badge variant="outline" className="text-[9px] h-4 px-1">CTR</Badge>}
                              <span className="text-gray-500 truncate">{inv.client}</span>
                            </div>
                            {inv.operationId && (
                              <button
                                onClick={() => navigate(`/admin/operations/${inv.operationId}`)}
                                className="p-0.5 text-amber-600 hover:text-amber-800"
                                data-testid={`zero-amount-open-${inv.operationId}`}
                                title="Ouvrir l'opération"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              {(() => {
                const paidNoPayment = inconsistencies.filter(inc => inc.type === 'paid_no_payment');
                if (paidNoPayment.length === 0) return null;
                return (
                  <div className="bg-white border border-amber-300 rounded">
                    <button
                      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-amber-50/50 transition-colors text-left"
                      onClick={() => setExpandedDebtor(expandedDebtor === '__inconsistencies__' ? null : '__inconsistencies__')}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                        <p className="text-xs font-medium text-amber-800">
                          {paidNoPayment.length} opérations "payées" sans paiement tracé
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">{paidNoPayment.length}</Badge>
                        {expandedDebtor === '__inconsistencies__' ? <ChevronDown className="w-3.5 h-3.5 text-amber-500" /> : <ChevronRight className="w-3.5 h-3.5 text-amber-500" />}
                      </div>
                    </button>
                    {expandedDebtor === '__inconsistencies__' && (
                      <div className="border-t border-amber-200 max-h-[200px] overflow-y-auto">
                        {paidNoPayment.map((inc, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 border-b border-amber-100 last:border-0 hover:bg-amber-50/60 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-gray-400 w-[70px] shrink-0">{inc.date || '—'}</span>
                              <span className="font-mono text-gray-600">{inc.reference}</span>
                              <span className="text-gray-500 truncate max-w-[120px]">{inc.client}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-amber-700">{inc.amount ? formatAmount(inc.amount) : '—'}</span>
                              <button onClick={() => navigate(`/admin/operations/${inc.operationId}`)}
                                className="p-0.5 text-amber-600 hover:text-amber-800">
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ═══════ 2bis. CONTRATS — À FACTURER (WIP) ═══════ */}
      {contractWIP.byContract && contractWIP.byContract.length > 0 && (
        <Card className="border border-violet-200 bg-violet-50/30" data-testid="contract-wip-block">
          <CardContent className="pt-4 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-violet-800 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-violet-500" />
                Contrats — à facturer ({contractWIP.byContract.length})
              </p>
              <span className="text-[10px] text-violet-600 uppercase tracking-wide">Revenus WIP</span>
            </div>
            <p className="text-xs text-violet-700 -mt-1">
              Opérations livrées en attente de facturation mensuelle au niveau du contrat.
              <span className="text-violet-600"> Ne sont pas comptées dans le recouvrement spot.</span>
            </p>
            <div className="space-y-1.5">
              {contractWIP.byContract.map((c) => {
                const isWarn = c.severity === 'warning';
                return (
                  <div
                    key={c.contractId}
                    className={`flex items-center justify-between bg-white border rounded px-3 py-2 hover:bg-violet-50 transition-colors ${isWarn ? 'border-orange-300' : 'border-violet-200'}`}
                    data-testid={`contract-wip-row-${c.contractId}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${isWarn ? 'bg-orange-500' : 'bg-violet-400'}`} />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-800 truncate">
                          {c.contractNumber || c.contractId.slice(0, 8)} · <span className="text-gray-600">{c.clientName}</span>
                        </p>
                        <p className="text-[10px] text-gray-500">
                          {c.unbilledRunsCount} run(s) · plus ancien : {c.oldestRunDate || '—'} ·
                          <span className={isWarn ? 'text-orange-600 font-semibold ml-1' : 'text-gray-500 ml-1'}>
                            {c.daysOverdue}j d'ancienneté
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-right">
                        <span className="font-mono text-xs text-violet-700 font-semibold block">
                          {c.unbilledAmount ? `${formatAmount(c.unbilledAmount)} XOF` : '—'}
                        </span>
                        {c.amountNote && (
                          <span className="text-[9px] text-violet-500 italic">{c.amountNote}</span>
                        )}
                      </div>
                      <button
                        onClick={() => navigate(`/admin/contracts/${c.contractId}`)}
                        className="text-xs text-violet-600 hover:text-violet-800 hover:underline flex items-center gap-1"
                        data-testid={`contract-wip-open-${c.contractId}`}
                      >
                        Facturer <ExternalLink className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ═══════ 2ter. ÉCART DE FACTURATION CONTRAT ═══════ */}
      {billingVariance.count > 0 && (
        <Card
          className={billingVariance.totalDelta < 0 ? "border border-red-200 bg-red-50/30" : "border border-indigo-200 bg-indigo-50/30"}
          data-testid="billing-variance-block"
        >
          <CardContent className="pt-4 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className={`text-xs font-semibold flex items-center gap-2 ${billingVariance.totalDelta < 0 ? 'text-red-800' : 'text-indigo-800'}`}>
                <span className={`w-2 h-2 rounded-full ${billingVariance.totalDelta < 0 ? 'bg-red-500' : 'bg-indigo-500'}`} />
                Écart de facturation contrat ({billingVariance.count})
              </p>
              <span className={`text-xs font-mono font-bold ${billingVariance.totalDelta < 0 ? 'text-red-700' : 'text-indigo-700'}`} data-testid="billing-variance-total">
                {billingVariance.totalDelta >= 0 ? '+' : ''}{formatAmount(billingVariance.totalDelta)} XOF
              </span>
            </div>
            <p className="text-xs text-gray-600 -mt-1">
              Factures contrat avec montant personnalisé différent du calcul automatique — audit des gestes commerciaux, corrections et négociations.
            </p>
            {/* Répartition par motif */}
            {Object.keys(billingVariance.byReason).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(billingVariance.byReason).map(([reason, delta]) => {
                  const reasonLabels = {
                    commercial_gesture: 'Geste commercial',
                    error_correction: 'Correction erreur',
                    partial_billing: 'Facturation partielle',
                    negotiated: 'Négocié',
                    other: 'Autre',
                    unknown: 'Non spécifié',
                  };
                  return (
                    <span key={reason} className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] rounded-full bg-white border border-gray-200 text-gray-700">
                      {reasonLabels[reason] || reason} :
                      <span className={`font-mono font-semibold ${delta < 0 ? 'text-red-600' : 'text-indigo-600'}`}>
                        {delta >= 0 ? '+' : ''}{formatAmount(delta)}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
            {/* Liste détaillée */}
            <div className="space-y-1">
              {billingVariance.items.map((v) => (
                <div
                  key={v.invoiceId}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-1.5 hover:bg-gray-50 text-xs"
                  data-testid={`billing-variance-row-${v.invoiceId}`}
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-mono text-gray-700 shrink-0">{v.invoiceNumber}</span>
                    <span className="text-gray-500 truncate">{v.clientName}</span>
                    {v.note && <span className="text-[10px] text-gray-400 italic truncate max-w-[200px]">· {v.note}</span>}
                  </div>
                  <div className="flex items-center gap-3 shrink-0 text-[11px]">
                    <span className="text-gray-400">calc. {formatAmount(v.calculatedHT)}</span>
                    <span className="text-gray-700">→ {formatAmount(v.billedHT)}</span>
                    <span className={`font-mono font-semibold ${v.delta < 0 ? 'text-red-600' : 'text-indigo-600'}`}>
                      {v.delta >= 0 ? '+' : ''}{formatAmount(v.delta)}
                      {v.deltaPct !== null && <span className="text-[9px] ml-0.5">({v.deltaPct >= 0 ? '+' : ''}{v.deltaPct}%)</span>}
                    </span>
                    <button
                      onClick={() => navigate(`/admin/contracts/${v.contractId}`)}
                      className="text-indigo-600 hover:text-indigo-800"
                      data-testid={`billing-variance-open-${v.invoiceId}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <Card data-testid="receivables-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-500" />
              Créances en cours ({filteredReceivables.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setStatementGenOpen(true)}
                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                data-testid="open-statement-generator-btn"
              >
                <FileBarChart className="w-3.5 h-3.5 mr-1.5" />
                Générer un relevé
              </Button>
              {recSearch || recAgingFilter !== 'all' ? (
                <Button variant="ghost" size="sm" className="text-xs text-gray-500 h-7" onClick={() => { setRecSearch(''); setRecAgingFilter('all'); }}>
                  <X className="w-3 h-3 mr-1" /> Réinitialiser
                </Button>
              ) : null}
            </div>
          </div>
          <CardDescription>Toutes les factures en attente — cliquez sur une ligne pour accéder à l'opération</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters row */}
          <div className="flex items-center gap-3 mb-3" data-testid="receivables-filters">
            <div className="flex-1">
              <Input placeholder="Rechercher par référence, client..." value={recSearch}
                onChange={(e) => setRecSearch(e.target.value)} className="h-8 text-xs border-gray-300" data-testid="receivables-search" />
            </div>
            <Select value={recAgingFilter} onValueChange={setRecAgingFilter}>
              <SelectTrigger className="w-[140px] h-8 text-xs border-gray-300" data-testid="receivables-aging-filter">
                <SelectValue placeholder="Ancienneté" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="0-30">0-30 jours</SelectItem>
                <SelectItem value="30-60">30-60 jours</SelectItem>
                <SelectItem value="60-90">60-90 jours</SelectItem>
                <SelectItem value="90+">+90 jours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Quick filter: top debtors as clickable chips */}
          {topDebtors.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="debtor-chips">
              <span className="text-[10px] text-gray-400 uppercase tracking-wide mr-1">Top débiteurs :</span>
              {topDebtors.slice(0, 5).map((d, i) => (
                <div key={d.clientName} className="inline-flex items-center group/chip">
                  <button
                    onClick={() => setRecSearch(recSearch === d.clientName ? '' : d.clientName)}
                    className={`inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-l-full text-xs font-medium border border-r-0 transition-colors ${
                      recSearch === d.clientName
                        ? 'bg-red-100 border-red-300 text-red-800'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-red-50 hover:border-red-200'
                    }`}
                    data-testid={`debtor-chip-${i}`}
                  >
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                      i === 0 ? 'bg-red-200 text-red-700' : i === 1 ? 'bg-orange-200 text-orange-700' : 'bg-gray-200 text-gray-600'
                    }`}>{i + 1}</span>
                    {d.clientName}
                    <span className="text-red-500 font-semibold">{formatExact(d.totalDue)}</span>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openStatementForClient({ id: d.clientId, companyName: d.clientName }); }}
                    className={`inline-flex items-center justify-center px-1.5 py-1 rounded-r-full border text-emerald-600 hover:text-white hover:bg-emerald-600 transition-colors ${
                      recSearch === d.clientName
                        ? 'bg-red-100 border-red-300'
                        : 'bg-gray-50 border-gray-200 hover:border-emerald-600'
                    }`}
                    title={`Relevé de compte de ${d.clientName}`}
                    data-testid={`debtor-statement-${i}`}
                  >
                    <FileBarChart className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="max-h-[500px] overflow-y-auto" data-testid="unpaid-invoices-list">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 font-medium">Ref Facture</th>
                  <th className="pb-2 font-medium">Opération / Contrat</th>
                  <th className="pb-2 font-medium cursor-pointer hover:text-gray-800" onClick={() => toggleRecSort('clientName')}>
                    Client {recSortField === 'clientName' ? (recSortDir === 1 ? '↑' : '↓') : ''}
                  </th>
                  <th className="pb-2 font-medium text-right cursor-pointer hover:text-gray-800" onClick={() => toggleRecSort('totalAmount')}>
                    Montant {recSortField === 'totalAmount' ? (recSortDir === 1 ? '↑' : '↓') : ''}
                  </th>
                  <th className="pb-2 font-medium text-right cursor-pointer hover:text-gray-800" onClick={() => toggleRecSort('remaining')}>
                    Reste dû {recSortField === 'remaining' ? (recSortDir === 1 ? '↑' : '↓') : ''}
                  </th>
                  <th className="pb-2 font-medium text-right cursor-pointer hover:text-gray-800" onClick={() => toggleRecSort('daysOutstanding')}>
                    Jours {recSortField === 'daysOutstanding' ? (recSortDir === 1 ? '↑' : '↓') : ''}
                  </th>
                  <th className="pb-2 font-medium text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredReceivables.map((inv, i) => {
                  const linkTo = inv.operationId ? `/admin/operations/${inv.operationId}` : inv.contractNumber ? `/admin/contracts` : null;
                  return (
                    <tr key={`${inv.reference}-${i}`}
                      className={`border-b border-gray-50 transition-colors ${linkTo ? 'hover:bg-blue-50/40 cursor-pointer' : 'hover:bg-gray-50/60'}`}
                      onClick={() => linkTo && navigate(linkTo)}>
                      <td className="py-2.5">
                        <span className="font-mono text-gray-700">{inv.reference}</span>
                        <Badge variant="outline" className="ml-1 text-[9px]">{inv.type === 'operation' ? 'OP' : 'CTR'}</Badge>
                      </td>
                      <td className="py-2.5">
                        {inv.operationId ? (
                          <span className="font-mono text-[#1db584] flex items-center gap-1">
                            {inv.operationNumber || '—'} <ExternalLink className="w-3 h-3" />
                          </span>
                        ) : inv.contractNumber ? (
                          <span className="font-mono text-blue-600 flex items-center gap-1 text-[10px]">
                            {inv.contractNumber} <ExternalLink className="w-3 h-3" />
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-2.5 text-gray-700 max-w-[160px] truncate font-medium">{inv.clientName}</td>
                      <td className="py-2.5 text-right text-gray-800 font-mono">{formatExact(inv.totalAmount)}</td>
                      <td className="py-2.5 text-right font-mono font-semibold text-red-600">{formatExact(inv.remaining)}</td>
                      <td className="py-2.5 text-right">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          inv.daysOutstanding > 60 ? 'bg-red-100 text-red-700' :
                          inv.daysOutstanding > 30 ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{inv.daysOutstanding}j</span>
                      </td>
                      <td className="py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          {inv.type === 'operation' && inv.operationId && (
                            <button onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(e, inv); }}
                              className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-[#1db584] transition-colors"
                              title="Télécharger la facture" data-testid={`download-invoice-${i}`}>
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {inv.clientId && (
                            <button
                              onClick={(e) => { e.stopPropagation(); openStatementForClient({ id: inv.clientId, companyName: inv.clientName }); }}
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-600 hover:text-emerald-700 transition-colors"
                              title={`Relevé de compte de ${inv.clientName}`}
                              data-testid={`row-statement-${i}`}
                            >
                              <FileBarChart className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredReceivables.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-green-600 text-sm">
                      {recSearch || recAgingFilter !== 'all' ? 'Aucun résultat pour ces filtres' : 'Aucune créance en cours'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>


      {/* ═══════ 3b. HISTORIQUE RELANCES ═══════ */}
      <Card data-testid="reminder-history">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-5 w-5 text-purple-500" />
              Historique des relances ({reminderLogs.length})
            </CardTitle>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => setShowLogForm(!showLogForm)} data-testid="log-manual-reminder-btn">
              <Plus className="w-3.5 h-3.5" /> Tracer une action
            </Button>
          </div>
          <CardDescription>Actions de recouvrement effectuées (automatiques et manuelles)</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Manual log form */}
          {showLogForm && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4 space-y-3" data-testid="manual-log-form">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase">Client *</Label>
                  <Input className="h-8 text-xs border-gray-300" placeholder="Nom du client" value={logForm.clientName}
                    onChange={(e) => setLogForm(p => ({ ...p, clientName: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase">Réf facture</Label>
                  <Input className="h-8 text-xs border-gray-300" placeholder="FACT-2026-0001" value={logForm.invoiceRef}
                    onChange={(e) => setLogForm(p => ({ ...p, invoiceRef: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase">Canal</Label>
                  <Select value={logForm.channel} onValueChange={(v) => setLogForm(p => ({ ...p, channel: v }))}>
                    <SelectTrigger className="h-8 text-xs border-gray-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Appel téléphonique</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="meeting">Réunion</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase">ID Opération</Label>
                  <Input className="h-8 text-xs border-gray-300" placeholder="(optionnel)" value={logForm.operationId}
                    onChange={(e) => setLogForm(p => ({ ...p, operationId: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500 uppercase">Note / Résumé *</Label>
                <Input className="h-8 text-xs border-gray-300" placeholder="Ex: Client promet paiement avant le 25/04..." value={logForm.note}
                  onChange={(e) => setLogForm(p => ({ ...p, note: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2 justify-end">
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowLogForm(false)}>Annuler</Button>
                <Button size="sm" className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={handleLogManualReminder} disabled={!logForm.clientName || !logForm.note} data-testid="confirm-log-btn">
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
          {/* Logs table */}
          {loadingLogs ? (
            <div className="py-6 text-center text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement...</div>
          ) : reminderLogs.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-400">
              Aucune relance enregistrée. Les relances automatiques et manuelles apparaîtront ici.
            </div>
          ) : (
            <div className="max-h-[320px] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-1.5 font-medium w-[100px]">Date</th>
                    <th className="pb-1.5 font-medium">Client</th>
                    <th className="pb-1.5 font-medium">Facture</th>
                    <th className="pb-1.5 font-medium w-[90px]">Canal</th>
                    <th className="pb-1.5 font-medium w-[80px]">Niveau</th>
                    <th className="pb-1.5 font-medium">Note / Action</th>
                    <th className="pb-1.5 font-medium w-[90px]">Par</th>
                  </tr>
                </thead>
                <tbody>
                  {reminderLogs.map((log, i) => {
                    const channelLabels = { email: 'Email', call: 'Appel', whatsapp: 'WhatsApp', meeting: 'Réunion', manual: 'Manuel', other: 'Autre' };
                    const channelColors = { email: 'bg-blue-100 text-blue-700', call: 'bg-green-100 text-green-700', whatsapp: 'bg-emerald-100 text-emerald-700', meeting: 'bg-purple-100 text-purple-700', manual: 'bg-gray-100 text-gray-700' };
                    const levelLabels = { 0: 'Rappel', 1: '1ère', 2: '2ème', 3: 'Finale', '-1': 'Manuel' };
                    const levelColors = { 0: 'text-blue-600', 1: 'text-amber-600', 2: 'text-orange-600', 3: 'text-red-600', '-1': 'text-purple-600' };
                    return (
                      <tr key={log.id || i} className="border-b border-gray-50 hover:bg-gray-50/60">
                        <td className="py-1.5 font-mono text-gray-500">{(log.sent_at || '').slice(0, 10)}</td>
                        <td className="py-1.5 text-gray-800 font-medium truncate max-w-[120px]">{log.clientName || '—'}</td>
                        <td className="py-1.5">
                          {log.invoiceRef ? (
                            <span className="font-mono text-gray-600">{log.invoiceRef}</span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${channelColors[log.channel] || channelColors.manual}`}>
                            {channelLabels[log.channel] || log.channel}
                          </span>
                        </td>
                        <td className="py-1.5">
                          <span className={`font-semibold ${levelColors[String(log.level)] || 'text-gray-600'}`}>
                            {levelLabels[String(log.level)] || `Niv.${log.level}`}
                          </span>
                        </td>
                        <td className="py-1.5 text-gray-600 truncate max-w-[200px]">{log.note || '—'}</td>
                        <td className="py-1.5 text-gray-400 truncate max-w-[90px]">{log.sent_by || 'Système'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══════ 4. ANALYTICS ═══════ */}
      {/* Aging Granulaire */}
      {agingGranular.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />Vieillissement des créances (granulaire)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {agingGranular.map((bucket, i) => (
                <button key={i} className="text-center p-3 rounded-xl border border-gray-100 hover:shadow-sm transition-shadow cursor-pointer"
                  style={{ borderTopColor: bucket.color, borderTopWidth: 3 }}
                  onClick={() => {
                    const map = { '0-10 j': '0-30', '11-20 j': '0-30', '21-30 j': '0-30', '31-60 j': '30-60', '61-90 j': '60-90', '> 90 j': '90+' };
                    setRecAgingFilter(map[bucket.label] || 'all');
                  }}>
                  <p className="text-xs font-semibold text-gray-600">{bucket.label}</p>
                  <p className="text-base font-bold text-gray-900 mt-1" title={`${formatExact(bucket.amount)} XOF`}>{formatExact(bucket.amount)}</p>
                  <p className="text-xs text-gray-400">XOF</p>
                  <p className="text-xs font-medium mt-1" style={{ color: bucket.color }}>{bucket.count} fact.</p>
                </button>
              ))}
            </div>
            <div className="flex h-3 rounded-full overflow-hidden mt-4">
              {agingGranular.filter(b => b.amount > 0).map((bucket, i) => {
                const total = agingGranular.reduce((s, b) => s + b.amount, 0);
                const pct = total > 0 ? (bucket.amount / total * 100).toFixed(1) : 0;
                return <div key={i} title={`${bucket.label}: ${pct}%`} style={{ width: `${pct}%`, backgroundColor: bucket.color }} />;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {monthlyUnpaid.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />Impayés par mois
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={monthlyUnpaid} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={formatAmount} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip formatter={(v) => [`${formatExact(v)} XOF`, 'Impayé']} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  <Bar dataKey="amount" fill="#ef4444" radius={[4, 4, 0, 0]} name="Impayé">
                    <LabelList dataKey="count" position="top" style={{ fontSize: 9, fill: '#6b7280' }} formatter={v => `${v} fact.`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
        {fastPayers.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-600" />Clients qui paient le plus vite
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {fastPayers.slice(0, 6).map((p, i) => {
                  const tagColor = p.tag === 'Excellent' ? 'bg-emerald-100 text-emerald-700' : p.tag === 'Bon' ? 'bg-blue-100 text-blue-700' : p.tag === 'Moyen' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
                  return (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">{i + 1}</span>
                        <div>
                          <p className="text-xs font-medium text-gray-800">{p.clientName}</p>
                          <p className="text-xs text-gray-400">{p.invoiceCount} factures · {formatExact(p.totalPaid)} XOF</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">{p.avgDays}j</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tagColor}`}>{p.tag}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ═══════ 5. FORECAST & DSO (only if data) ═══════ */}
      {(forecast.weeks?.some(w => w.amount > 0) || forecast.overdueCount > 0 || clientDSO.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {(forecast.weeks?.some(w => w.amount > 0) || forecast.overdueCount > 0) && (
            <Card data-testid="cash-forecast">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-500" /> Prévision encaissements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {forecast.overdueCount > 0 && (
                  <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded px-3 py-2 mb-3" data-testid="overdue-banner">
                    <span className="text-sm text-red-800 font-medium">En retard</span>
                    <div className="text-right">
                      <span className="text-lg font-bold text-red-700" title={`${formatExact(forecast.overdueAmount)} XOF`}>{formatExact(forecast.overdueAmount)}</span>
                      <span className="text-xs text-red-600 ml-1">({forecast.overdueCount} fact.)</span>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {forecast.weeks.filter(w => w.amount > 0).map((w, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <span className="text-sm font-medium text-gray-800">{w.label}</span>
                        <span className="text-xs text-gray-400 ml-2">{w.start} — {w.end}</span>
                      </div>
                      <span className="font-mono font-semibold text-gray-900">{formatExact(w.amount)} <span className="text-xs text-gray-400">({w.count})</span></span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {clientDSO.length > 0 && (
            <Card data-testid="client-dso">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" /> DSO par client
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[260px] overflow-y-auto">
                  {clientDSO.map((c, i) => (
                    <button key={i} className="w-full flex items-center justify-between py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 rounded transition-colors text-left"
                      onClick={() => setRecSearch(recSearch === c.clientName ? '' : c.clientName)}>
                      <div>
                        <span className="text-sm font-medium text-gray-800 truncate max-w-[180px] block">{c.clientName}</span>
                        <span className="text-xs text-gray-400">{c.count} facture{c.count > 1 ? 's' : ''} · {formatExact(c.outstanding)}</span>
                      </div>
                      <span className={`text-lg font-bold font-mono ${c.avgDays > 60 ? 'text-red-600' : c.avgDays > 30 ? 'text-amber-600' : 'text-gray-800'}`}>{c.avgDays}j</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ═══════ 6. REPORT CONFIG ═══════ */}
      <Card className="border-dashed border-[#1db584]/40">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-[#1db584]" />
              <div>
                <p className="text-sm font-semibold text-gray-800">Rapport Hebdomadaire</p>
                <p className="text-xs text-gray-500">
                  {reportConfig.enabled
                    ? `Actif - ${DAYS.find(d => d.value === String(reportConfig.day_of_week))?.label || 'Mardi'} - ${reportConfig.recipients.length} destinataire(s)`
                    : 'Desactive'}
                  {reportConfig.last_sent_at && ` - Dernier envoi: ${new Date(reportConfig.last_sent_at).toLocaleDateString('fr-FR')}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={sendNow} disabled={sending || !reportConfig.recipients?.length} data-testid="send-report-now-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Envoyer maintenant
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowConfig(!showConfig)} data-testid="toggle-report-config-btn">
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {showConfig && (
            <div className="mt-4 pt-4 border-t space-y-3" data-testid="report-config-panel">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reportConfig.enabled}
                    onChange={e => setReportConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="rounded border-gray-300 text-[#1db584] focus:ring-[#1db584]"
                    data-testid="report-enabled-checkbox"
                  />
                  <span className="text-sm text-gray-700">Envoi automatique active</span>
                </label>
                <Select value={String(reportConfig.day_of_week)} onValueChange={v => setReportConfig(prev => ({ ...prev, day_of_week: parseInt(v) }))}>
                  <SelectTrigger className="w-36 h-8 text-xs" data-testid="report-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Destinataires</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {reportConfig.recipients.map(email => (
                    <Badge key={email} variant="secondary" className="text-xs gap-1 pr-1" data-testid={`recipient-${email}`}>
                      {email}
                      <button onClick={() => removeRecipient(email)} className="ml-0.5 hover:text-red-600"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="email@exemple.com"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addRecipient()}
                    className="h-8 text-xs flex-1"
                    data-testid="new-recipient-input"
                  />
                  <Button variant="outline" size="sm" onClick={addRecipient} className="h-8" data-testid="add-recipient-btn">
                    <Plus className="h-3 w-3 mr-1" />Ajouter
                  </Button>
                </div>
              </div>
              <Button size="sm" onClick={saveConfig} disabled={savingConfig} className="bg-[#1db584] hover:bg-[#19a074]" data-testid="save-report-config-btn">
                {savingConfig ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Sauvegarder
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Relances Automatiques */}
      <ReminderPanel token={token} />

      {/* Statement Generator Modal (client picker) */}
      <StatementGeneratorModal
        open={statementGenOpen}
        onOpenChange={setStatementGenOpen}
        onClientPicked={openStatementForClient}
      />

      {/* Client Statement Dialog (preview + download) */}
      <ClientStatementDialog
        open={statementDialogOpen}
        onOpenChange={setStatementDialogOpen}
        client={statementTarget}
      />
    </div>
  );
};

export default RecouvrementKPIs;
